import React, { useCallback, useMemo } from 'react';

import type { CellClassParams, ColDef, DragStoppedEvent } from 'ag-grid-community';
import { compact } from 'lodash';
import { constSelector, useRecoilValue } from 'recoil';
import { useTheme } from 'styled-components';
import type { Analysis, CustomizedBlock, PerformanceSummary } from 'venn-api';
import {
  blockBenchmarkType,
  type BlockId,
  flattenedProxyMapState,
  vdparFundIdsState,
  isReturnsBasedBlockState,
  portfolioStrategyFilterType,
  type StudioAnalysisRequest,
} from 'venn-state';
import { useMemoWithArgs } from 'venn-utils';
import { useGetRequestSubjectFee } from './use-advisory-fee';
import {
  type AnalysisResponseParser,
  autoGroupColumnDefStaticConstants,
  AVAILABILITY_KEY,
  BENCHMARK_KEY,
  FREQUENCY_KEY,
  getAvailableRangeValue,
  getBenchmarkNameRangeMap,
  getMaxFrequencyValue,
  handleMetricsReorder,
  ITALIC_CLASS,
  type PortfolioRowData,
  useAgGridStyle,
} from '../../../studio-blocks';
import { getGroupColDef } from '../../../studio-blocks/logic/getGroupColDef';
import { isPortfolioRowData } from '../../../studio-blocks/logic/typeUtils';
import { useLabelColumnWidth } from '../../../studio-blocks/logic/useLabelColumnMaxWidth';
import { getRangeMapKeyFromRequest, useRequestsRangeMap } from '../../../studio-blocks/logic/useRequestsRangeMap';
import {
  renderSuperscripts,
  usePageProxySymbol,
  usePageVdparSymbol,
} from '../../components/core/block-subject-footnotes-and-superscripts';
import { OptionIcon } from '../../../common/OptionIcon';
import { usePortfolioMultiFiltering } from './use-portfolio-multi-filtering';

const autoColumnCellClass = ({ data }: CellClassParams) => compact(data.isMetadata ? ITALIC_CLASS : undefined);

const emptyObjectConstSelector = constSelector({});

export const useAnalysisTreeGrid = (
  requests: StudioAnalysisRequest[],
  responseParser: AnalysisResponseParser,
  analyses: (Analysis | undefined)[][],
  blockId: BlockId,
  selectedBlock: CustomizedBlock,
  onReorderMetrics: (newMetricsOrder: string[]) => void,
  inPrintMode?: boolean,
) => {
  const { hasMultiLevelPortfolioFiltering, filterRowData } = usePortfolioMultiFiltering(blockId);

  const proxySymbol = usePageProxySymbol();
  const vdparSymbol = usePageVdparSymbol();
  const customBenchmarkType = useRecoilValue(blockBenchmarkType(blockId));
  const portfolioStrategyFilter = useRecoilValue(portfolioStrategyFilterType(blockId));

  const rangeMap = useRequestsRangeMap(requests);
  const isReturnsBasedBlock = useRecoilValue(isReturnsBasedBlockState(blockId));
  const proxyMap: Record<string, string | undefined> = useRecoilValue(
    isReturnsBasedBlock ? flattenedProxyMapState : emptyObjectConstSelector,
  );
  const vdparFundIds = useRecoilValue(vdparFundIdsState);
  const theme = useTheme();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const rawRowData = useMemo(() => {
    const subjectIds = requests.map(getRangeMapKeyFromRequest);
    const rows: PortfolioRowData<PerformanceSummary | string | undefined>[] = inPrintMode
      ? []
      : compact([
          {
            label: AVAILABILITY_KEY,
            key: AVAILABILITY_KEY,
            path: [AVAILABILITY_KEY],
            value: getAvailableRangeValue(rangeMap, subjectIds),
            isStrategy: false,
            allocation: [],
            isMetadata: true,
          },
          {
            label: FREQUENCY_KEY,
            key: FREQUENCY_KEY,
            path: [FREQUENCY_KEY],
            value: getMaxFrequencyValue(rangeMap, subjectIds),
            isStrategy: false,
            allocation: [],
            isMetadata: true,
          },
          customBenchmarkType === 'COMMON' || customBenchmarkType === 'INDIVIDUAL'
            ? {
                label: BENCHMARK_KEY,
                key: BENCHMARK_KEY,
                path: [BENCHMARK_KEY],
                value: requests.map((request) => request.benchmark?.name),
                customBenchmarkType,
                benchmarkNameRangeMap: getBenchmarkNameRangeMap(rangeMap, requests),
                isStrategy: false,
                allocation: [],
                isMetadata: true,
              }
            : undefined,
        ]);
    return [...rows, ...responseParser.parser(selectedBlock, analyses)];
  }, [requests, inPrintMode, rangeMap, customBenchmarkType, responseParser, selectedBlock, analyses]);

  const rowData = useMemo(
    () =>
      hasMultiLevelPortfolioFiltering
        ? filterRowData(rawRowData)
        : rawRowData.filter((row) => {
            if (
              !portfolioStrategyFilter ||
              portfolioStrategyFilter === 'DEFAULT' ||
              !isPortfolioRowData(row) ||
              row.isMetadata
            ) {
              return true;
            }
            if (!row.isStrategy) {
              return false;
            }
            return portfolioStrategyFilter === 'TOP_LEVEL_STRATEGIES' ? row.path.length === 1 : true;
          }),
    [portfolioStrategyFilter, rawRowData, filterRowData, hasMultiLevelPortfolioFiltering],
  );

  const metricsSettings = responseParser.metricsSettings;

  const gridStyle = useAgGridStyle();
  // TODO(VENN-19715): would like to make getGroupColDef a hook, but it is currently used imperatively by the metricsSettingsColumnParser.
  const columnDefs = useMemoWithArgs(getGroupColDef, [
    requests,
    analyses,
    metricsSettings,
    theme,
    gridStyle,
    selectedBlock?.relativeToBenchmark,
    selectedBlock?.contributionToPercentage,
    selectedBlock?.selectedMetrics,
    selectedBlock?.selectedFactors,
    undefined,
    getRequestSubjectFee,
  ]);

  const onDragStoped = useCallback(
    (e: DragStoppedEvent) => {
      handleMetricsReorder(e, onReorderMetrics, columnDefs);
    },
    [columnDefs, onReorderMetrics],
  );

  const { minWidth: labelColumnMinWidth, maxWidth: labelColumnMaxWidth } = useLabelColumnWidth(columnDefs);

  const autoGroupColumnDef = useMemo(
    (): ColDef => ({
      ...autoGroupColumnDefStaticConstants,
      minWidth: labelColumnMinWidth,
      maxWidth: labelColumnMaxWidth,
      cellClass: autoColumnCellClass,
      cellRenderer: ({ data }: { data: PortfolioRowData<PerformanceSummary | string | undefined> }) => {
        if (!data.fundId && !data.optionData) {
          return data.label;
        }
        const icon = data.optionData ? <OptionIcon optionType={data.optionData.optionType} /> : null;

        return (
          <>
            {data.label}
            {icon && <> {icon}</>}
            {renderSuperscripts([
              { condition: !!data.fundId && !!proxyMap?.[data.fundId], symbol: proxySymbol },
              { condition: !!data.fundId && vdparFundIds.has(data.fundId), symbol: vdparSymbol },
            ])}
          </>
        );
      },
      autoHeight: true,
    }),
    [labelColumnMinWidth, labelColumnMaxWidth, proxyMap, proxySymbol, vdparFundIds, vdparSymbol],
  );

  return {
    rowData,
    columnDefs,
    autoGroupColumnDef,
    onDragStoped,
  };
};
